import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  Card,
  CardMedia,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useAuth } from "../../../context/AuthContext";
import { useSubmitAssignmentQuery } from "../../../api/useSubmitAssignment";
import { useQueryClient } from "@tanstack/react-query";

const AssignmentDialog = ({ open, onClose, currentAssignment }) => {
  // State for message and submissionLinks
  const queryClient = useQueryClient();
  const cloudinaryRef = useRef();
  const widgetRef = useRef();
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submissionLinks, setSubmissionLinks] = useState(
    currentAssignment?.submissionLinks?.map((link) => ({
      title: link.title,
      detail: link.detail,
      type: link.type,
      link: "", // 또는 link.link 등 초기 링크 값이 있다면 여기에 설정
    })) || []
  );
  useEffect(() => {
    // currentAssignment가 업데이트될 때마다 submissionLinks 상태도 업데이트
    if (currentAssignment && currentAssignment.submissionLinks) {
      setSubmissionLinks(
        currentAssignment.submissionLinks.map((link) => ({
          title: link.title,
          detail: link.detail,
          type: link.type,
          link: "", // 또는 link.link 등 초기 링크 값이 있다면 여기에 설정
        }))
      );
    }
  }, [currentAssignment]);

  // Cloudinary 위젯 초기화
  useEffect(() => {
    if (typeof window !== "undefined" && window.cloudinary) {
      cloudinaryRef.current = window.cloudinary;
      widgetRef.current = cloudinaryRef.current.createUploadWidget(
        {
          cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
          multiple: false,
          resourceType: "image",
          clientAllowedFormats: ["png", "jpg", "jpeg", "gif", "webp"],
          maxImageFileSize: 10000000, // 10MB
          folder: "study-assignments",
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            // 업로드 성공 시 처리는 개별 핸들러에서 수행
          }
        }
      );
    }
  }, []);

  useEffect(() => {
    setErrorMessage("");
  }, [open]);
  const { mutate: submit, isPending } = useSubmitAssignmentQuery();
  const handleLinkChange = (index, field, value) => {
    const updatedLinks = submissionLinks.map((link, linkIndex) =>
      index === linkIndex ? { ...link, [field]: value } : link
    );
    setSubmissionLinks(updatedLinks);
  };

  // 이미지 업로드 핸들러
  const handleImageUpload = (index) => {
    if (widgetRef.current) {
      // 기존 콜백 제거하고 새로운 콜백 설정
      widgetRef.current.destroy();
      widgetRef.current = cloudinaryRef.current.createUploadWidget(
        {
          cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
          multiple: false,
          resourceType: "image",
          clientAllowedFormats: ["png", "jpg", "jpeg", "gif", "webp"],
          maxImageFileSize: 10000000, // 10MB
          folder: "study-assignments",
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            handleLinkChange(index, "link", result.info.secure_url);
          }
        }
      );
      widgetRef.current.open();
    }
  };

  // 이미지 삭제 핸들러
  const handleImageDelete = (index) => {
    handleLinkChange(index, "link", "");
  };

  const handleSubmit = () => {
    const data = {
      message: message,
      submissionLinks: submissionLinks,
    };
    // TODO: Submit 'data' to the server

    submit(
      { data, agendaId: currentAssignment._id },
      {
        onSuccess: () => {
          onClose(); // 성공 시 모달 닫기
          setErrorMessage("");
          queryClient.invalidateQueries("assignment");
        },
        onError: (error) => {
          setErrorMessage(
            error.response.data.message || "등록에 실패했습니다."
          ); // 실패 시 에러 메시지 설정
        },
      }
    );
    setMessage("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="assignment-dialog-title"
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "80%",
          width: "auto",
        },
      }}
    >
      <DialogTitle id="assignment-dialog-title">
        {currentAssignment.assignment}
      </DialogTitle>

      <DialogContent dividers>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {submissionLinks &&
          submissionLinks.map((link, index) => (
            <React.Fragment key={index}>
              <Typography variant="subtitle1">{link.title}</Typography>
              <Typography variant="subtitle2" gutterBottom>
                {link.detail}
              </Typography>

              {link.type === "image" ? (
                <Box sx={{ mb: 2 }}>
                  {submissionLinks[index].link ? (
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <Card sx={{ maxWidth: 300, mb: 1 }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={submissionLinks[index].link}
                          alt={link.title}
                          sx={{ objectFit: "contain" }}
                        />
                      </Card>
                      <IconButton
                        onClick={() => handleImageDelete(index)}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          bgcolor: "error.main",
                          color: "white",
                          "&:hover": {
                            bgcolor: "error.dark",
                          },
                          width: 24,
                          height: 24,
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => handleImageUpload(index)}
                      sx={{ mb: 1 }}
                      fullWidth
                    >
                      이미지 업로드
                    </Button>
                  )}
                </Box>
              ) : (
                <TextField
                  autoFocus
                  margin="dense"
                  id={`link-${index}`}
                  label={link.title}
                  type="url"
                  fullWidth
                  variant="outlined"
                  value={submissionLinks[index].link}
                  onChange={(e) =>
                    handleLinkChange(index, "link", e.target.value)
                  }
                  required={true}
                />
              )}
            </React.Fragment>
          ))}
        <React.Fragment>
          <Typography variant="subtitle1">개선사항</Typography>
          <Typography variant="subtitle2" gutterBottom>
            오늘 강의에 개선사항이 있으면 알려주세요!
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="message"
            label="개선사항"
            type="text"
            fullWidth
            variant="outlined"
            value={message}
            multiline
            minRows={6}
            onChange={(e) => setMessage(e.target.value)}
          />
        </React.Fragment>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        <Button
          onClick={() => {
            handleSubmit();
          }}
          disabled={isPending}
        >
          {isPending ? "제출중" : "제출하기"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentDialog;
